import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useFunction } from "@project-rouge/rg-three";
import { Html, Line } from "@react-three/drei";
import { memo, useMemo, useEffect } from "react";
import { MathUtils, DoubleSide } from "three";
import { Extrude } from "./index45.js";
import { Shape } from "./index47.js";
import { fontFamily } from "./index219.js";
import { getBuildingCenter } from "./index221.js";
import { OutClick } from "./index171.js";
import { getWingPoly } from "./index175.js";
const max = 25;
const keys = ["Enter", "Escape", "ArrowUp", "ArrowDown"];
const WingEditor = memo(({
  building,
  bone,
  onAccept,
  onCancel,
  enableKeyboardControls,
  disableKeyboardControls,
  wingLevelsInput,
  f2fHeight,
  topOffset,
  minFloorNumber,
  floors,
  setFloors
}) => {
  const boneCenter = useMemo(() => {
    const boneCenter2 = getBuildingCenter(building, bone);
    boneCenter2[1] *= 2;
    return boneCenter2;
  }, [building, bone, bone.floors]);
  const wingPoly = useMemo(() => getWingPoly({ building, bone, level: bone.floors - 1 }), [building, bone, bone.floors]);
  const onChange = useFunction((floors2) => {
    setFloors(floors2);
  });
  const newTop = boneCenter[1] + topOffset - bone.floors * f2fHeight + floors * f2fHeight;
  const _onAccept = useFunction(() => {
    if (floors === bone.floors)
      return onCancel();
    onAccept({ floors, boneId: bone.id, buildingId: building.uuid });
  });
  const onKeyUp = useFunction((e) => {
    if (!keys.includes(e.key))
      return;
    e.preventDefault();
    if (e.key === "Enter")
      _onAccept();
    if (e.key === "Escape")
      onCancel();
    if (e.key === "ArrowUp")
      setFloors(Math.min(max, floors + 1));
    if (e.key === "ArrowDown")
      setFloors(Math.max(minFloorNumber, floors - 1));
  });
  useEffect(() => {
    disableKeyboardControls && disableKeyboardControls();
    document.body.addEventListener("keyup", onKeyUp);
    return () => {
      document.body.removeEventListener("keyup", onKeyUp);
      enableKeyboardControls && enableKeyboardControls();
    };
  }, [enableKeyboardControls, disableKeyboardControls]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(OutClick, { onClick: _onAccept }),
    /* @__PURE__ */ jsx(
      Counter,
      {
        position: boneCenter,
        floors,
        bone,
        newTop
      }
    ),
    /* @__PURE__ */ jsx(
      TransparentWing,
      {
        bone,
        floors,
        wingPoly,
        f2fHeight,
        top: boneCenter[1] + topOffset,
        newTop
      }
    ),
    /* @__PURE__ */ jsx(Html, { position: boneCenter, center: true, style: { marginTop: 100 }, children: wingLevelsInput({ value: floors, onChange, min: minFloorNumber }) })
  ] });
});
WingEditor.displayName = "WingEditor";
const Counter = ({ position, floors, bone, newTop }) => /* @__PURE__ */ jsxs(
  Html,
  {
    position: [position[0], newTop, position[2]],
    center: true,
    style: {
      fontFamily,
      fontSize: 14,
      fontWeight: 400,
      textShadow: "#fff 0px 0px 3px"
    },
    children: [
      floors > bone.floors ? "+" : "",
      floors - bone.floors
    ]
  }
);
const TransparentWing = ({ bone, floors, wingPoly, f2fHeight, top, newTop }) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(
    Shape,
    {
      name: "new top",
      rotation: [90 * MathUtils.DEG2RAD, 0, 0],
      position: [0, newTop, 0],
      points: wingPoly,
      "material-side": DoubleSide,
      "material-transparent": true,
      "material-opacity": floors >= bone.floors ? 0.8 : 1,
      "material-depthTest": false,
      "material-depthWrite": false,
      "material-color": "#EBECED",
      renderOrder: 12
    }
  ),
  floors !== bone.floors && /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
    Line,
    {
      name: "old top",
      position: [0, top, 0],
      rotation: [90 * MathUtils.DEG2RAD, 0, 0],
      points: wingPoly,
      color: "#3B3B5F",
      opacity: 0.5,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      renderOrder: 11
    }
  ) }),
  Math.abs(floors - bone.floors) > 1 && /* @__PURE__ */ jsx(Fragment, { children: Array(Math.abs(floors - bone.floors)).fill(0).map((_, i) => /* @__PURE__ */ jsx(
    Line,
    {
      name: "new floors except top",
      position: [0, newTop + (i + 1) * f2fHeight * (floors < bone.floors ? 1 : -1), 0],
      rotation: [90 * MathUtils.DEG2RAD, 0, 0],
      points: wingPoly,
      opacity: 0.5,
      color: "#3B3B5F",
      transparent: true,
      dashed: true,
      dashSize: 0.5,
      gapSize: 0.5,
      depthTest: false,
      depthWrite: false,
      renderOrder: floors < bone.floors ? 13 : 11
    },
    i
  )) }),
  /* @__PURE__ */ jsx(
    Line,
    {
      name: "new top",
      position: [0, newTop, 0],
      rotation: [90 * MathUtils.DEG2RAD, 0, 0],
      points: wingPoly,
      color: "#3B3B5F",
      transparent: true,
      depthTest: false,
      depthWrite: false,
      renderOrder: 11
    }
  ),
  /* @__PURE__ */ jsx(
    Extrude,
    {
      position: [0, top, 0],
      rotation: [90 * MathUtils.DEG2RAD, 0, 0],
      points: wingPoly,
      "material-side": DoubleSide,
      "material-transparent": true,
      "material-color": "#EBECED",
      "material-opacity": 0.3,
      "material-depthTest": false,
      "material-depthWrite": false,
      renderOrder: 10,
      extrude: {
        steps: 1,
        depth: -(floors - bone.floors) * f2fHeight,
        bevelEnabled: false
      }
    }
  )
] });
export {
  WingEditor
};
//# sourceMappingURL=index172.js.map
